<template>
  <div class="main">
    <!-- title -->
    <h3 class="title" content="edit" placement="bottom">
      Notification Management
      <el-button
        style="width: 180px"
        type="primary"
        @click="handleAdd()"
        v-if="$store.state.userLevel == 1"
        >Create</el-button
      >
      <span class="search-toggle" @click="searchExpanded = !searchExpanded">
        {{ searchExpanded ? "Hide Filters" : "Show Filters" }}
        <em class="el-icon-arrow-up" :class="{ 'is-collapsed': !searchExpanded }"></em>
      </span>
    </h3>

    <!-- search -->
    <div class="searchBox" v-show="searchExpanded">
      <el-form :model="formInline" ref="formInline" class="demo-form-inline">
        <div class="search-grid">
          <el-form-item prop="title">
            <el-input
              v-model="formInline.title"
              placeholder="Title"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
          <el-form-item prop="category">
            <el-input
              v-model="formInline.category"
              placeholder="Notification Category"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
        </div>
      </el-form>
      <div class="search-toolbar">
        <div class="search-toolbar__actions">
          <el-button type="primary" class="search" @click="submitSearch">Search</el-button>
          <el-button class="reset" @click="resetForm('formInline')"
            >Reset</el-button
          >
        </div>
      </div>
    </div>
    <!-- toolbar, table & pagination share one panel -->
    <div class="client-panel">
      <div class="client-panel__toolbar">
        <div class="search-toolbar__extra">
          <columns-control
            :columns="columnsConfig"
            storage-key="cms.notificationManagement.columns"
            @change="onColumnsChange"
          ></columns-control>
          <!-- filter & sort by -->
          <el-dropdown trigger="click" ref="filter">
          <span class="el-dropdown-link sortbox">
            <em class="filter"></em>
            Filter
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-form
              ref="formFilter"
              :model="formFilter"
              style="width: 300px; padding: 14px 24px"
            >
              <el-form-item
                label="Notification Type"
                style="margin-bottom: 12px"
                prop="type"
              >
                <el-select
                  v-model="formFilter.type"
                  :popper-append-to-body="false"
                >
                  <el-option value="All">All</el-option>
                  <el-option label="System Updates" value="SYSTEM"></el-option>
                  <el-option
                    label="Investment Updates"
                    value="INVESTMENT"
                  ></el-option>
                  <el-option
                    label="Corporate Updates"
                    value="CORPORATE"
                  ></el-option>
                  <el-option label="Market Updates" value="MARKET"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item
                label="Sales Office"
                style="margin-bottom: 12px"
                prop="salesOffice"
              >
                <el-select
                  v-model="formFilter.salesOffice"
                  :popper-append-to-body="false"
                  @change="changeSalesOffice"
                  :disabled="$store.state.userLevel >= 2"
                >
                  <el-option value="All">All</el-option>
                  <el-option
                    v-for="(item, index) in salesOfficeList"
                    :key="index"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item
                label="Region"
                style="margin-bottom: 12px"
                prop="region"
              >
                <el-select
                  v-model="formFilter.region"
                  :popper-append-to-body="false"
                >
                  <el-option value="All">All</el-option>
                  <el-option
                    v-for="(item, index) in regionList"
                    :key="index"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
              <div class="filterButton">
                <el-form-item style="margin-right: 15px; margin-bottom: 0">
                  <el-button
                    class="reset"
                    @click="
                      $refs.filter.hide();
                      resetForm('formFilter');
                    "
                    >Reset</el-button
                  >
                </el-form-item>
                <el-form-item style="margin-bottom: 0">
                  <el-button
                    type="primary"
                    class="search"
                    @click="
                      () => {
                        $refs.filter.hide();
                        submitSearch();
                      }
                    "
                    >Apply</el-button
                  >
                </el-form-item>
              </div>
            </el-form>
          </el-dropdown-menu>
        </el-dropdown>
        <el-dropdown trigger="click" @command="handleCommand">
          <span class="el-dropdown-link sortbox">
            <em class="sort"></em>
            Sort by
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              :command="['updatedAt,desc', 'notificationId,Desc']"
              >Last update date (New - Old)</el-dropdown-item
            >
            <el-dropdown-item
              :command="['updatedAt,asc', 'notificationId,Desc']"
              >Last update date (Old - New)</el-dropdown-item
            >
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- table -->
    <el-table :data="tableData">
      <el-table-column
        v-if="isColumnVisible('notificationId')"
        label="Notification ID"
        prop="notificationId"
      ></el-table-column>
      <el-table-column v-if="isColumnVisible('type')" label="Notification Type">
        <template slot-scope="scope">{{
          $commonjs.pushTypeMap(scope.row.type)
        }}</template>
      </el-table-column>
      <el-table-column
        v-if="isColumnVisible('category')"
        label="Notification Category"
        prop="category"
      ></el-table-column>
      <el-table-column v-if="isColumnVisible('salesOffice')" label="Sales Office">
        <template slot-scope="scope">{{
          $commonjs.salesOfficeMap(scope.row.salesOffice)
        }}</template>
      </el-table-column>
      <el-table-column v-if="isColumnVisible('region')" label="Region">
        <template slot-scope="scope">{{
          handleTableRegion(scope.row.regions)
        }}</template>
      </el-table-column>
      <el-table-column v-if="isColumnVisible('title')" prop="titles.EN" label="Title"></el-table-column>
      <el-table-column
        v-if="isColumnVisible('deliveryDate')"
        prop="deliveryDate"
        label="Delivery Date & Time"
        :formatter="$commonjs.tableFormatDateTime"
      ></el-table-column>
      <el-table-column v-if="isColumnVisible('status')" label="Status">
        <template slot-scope="scope">
          <span
            :style="
              scope.row.status == 'PENDING'
                ? 'color:rgb(85,130,160)'
                : scope.row.status == 'DELIVERED'
                ? 'color:rgb(0,127,80)'
                : 'color:rgb(255,0,0)'
            "
            >{{ $commonjs.pushStatusMap(scope.row.status) }}</span
          >
        </template>
      </el-table-column>
      <el-table-column
        v-if="isColumnVisible('updatedAt')"
        prop="updatedAt"
        label="Last update"
        :formatter="$commonjs.tableFormatDateTime"
      ></el-table-column>
      <el-table-column label="Operation">
        <template slot-scope="scope">
          <div class="btnFlex">
            <el-tooltip
              class="item"
              effect="dark"
              content="Edit"
              placement="top"
            >
              <em
                class="edit"
                style="margin-right: 10px"
                @click="handleEdit(scope.row)"
              ></em>
            </el-tooltip>
            <el-tooltip
              class="item"
              effect="dark"
              content="Revoke"
              placement="top"
              v-if="
                scope.row.status != 'REVOKED_PENDING' &&
                  scope.row.status != 'REVOKED_DELIVERED'
              "
            >
              <em class="revoke" @click="handleRevoke(scope.row)"></em>
            </el-tooltip>
            <el-button size="mini" @click="handleAddClone(scope.row)"
              >Clone</el-button
            >
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- page -->
    <app-pagination
      @size-change="
        v => {
          currentSize = v;
          currentPage = 1;
          getList();
        }
      "
      @current-change="
        v => {
          currentPage = v;
          getList();
        }
      "
      :current-page="currentPage"
      :page-sizes="[5, 10, 20, 50]"
      :total="total"
      :page-size="currentSize"
    ></app-pagination>
    </div>
  </div>
</template>

<script>
import {
  notificationList,
  notificationRevoke,
  getSalesOffice,
  getSalesRegion
} from "@/request/api";
export default {
  name: "notification",
  data() {
    return {
      tableData: [],
      currentSize: 10,
      currentPage: 1,
      total: 0,
      sort: ["updatedAt,desc", "notificationId,Desc"],
      formLabelWidth: "96px",
      formUser: {
        id: 0,
        username: "",
        password: "",
        roles: []
      },
      formInline: {
        title: "",
        category: ""
      },
      formFilter: {
        type: "All",
        salesOffice: "All",
        region: "All"
      },
      typeList: [],
      salesOfficeList: [],
      regionList: [],
      postList: [
        { name: "Online", code: true },
        { name: "Offline", code: false }
      ],
      searchExpanded: true,
      columnsConfig: [
        { prop: "notificationId", label: "Notification ID", visible: true },
        { prop: "type", label: "Notification Type", visible: true },
        { prop: "category", label: "Notification Category", visible: true },
        { prop: "salesOffice", label: "Sales Office", visible: true },
        { prop: "region", label: "Region", visible: true },
        { prop: "title", label: "Title", visible: true },
        { prop: "deliveryDate", label: "Delivery Date & Time", visible: true },
        { prop: "status", label: "Status", visible: true },
        { prop: "updatedAt", label: "Last update", visible: true },
      ],
      visibleColumnProps: [
        "notificationId",
        "type",
        "category",
        "salesOffice",
        "region",
        "title",
        "deliveryDate",
        "status",
        "updatedAt",
      ],
    };
  },
  methods: {
    isColumnVisible(prop) {
      return this.visibleColumnProps.includes(prop);
    },
    onColumnsChange(visibleProps) {
      this.visibleColumnProps = visibleProps;
    },
    getList() {
      let params = {
        page: this.currentPage - 1,
        size: this.currentSize,
        sort: this.sort
      };
      if (this.formInline.title) params["title"] = this.formInline.title;
      if (this.formInline.category)
        params["category"] = this.formInline.category;
      if (this.formFilter.type && this.formFilter.type != "All")
        params["type"] = this.formFilter.type;
      if (this.formFilter.salesOffice && this.formFilter.salesOffice != "All")
        params["salesOffice"] = this.formFilter.salesOffice;
      if (this.formFilter.region && this.formFilter.region != "All")
        params["region"] = this.formFilter.region;
      notificationList(params).then(res => {
        this.tableData = res.data.content;
        this.total = res.data.totalElements;
      });
    },
    getFilterConfig() {
      getSalesOffice().then(res => {
        this.salesOfficeList = res.data.payload;
      });
    },
    changeSalesOffice(v) {
      if (v == "All") {
        this.clearRegion();
      } else {
        this.clearRegion();
        getSalesRegion(v).then(res => {
          this.regionList = res.data.payload;
        });
      }
    },
    clearRegion() {
      this.regionList = [];
      this.$set(this.formFilter, "region", "");
    },
    handleAdd() {
      this.$router.push({ path: "/createNotification" });
    },
    handleEdit(row) {
      this.$router.push({ path: "/editNotification/" + row.id });
    },
    handleAddClone(row) {
      this.$router.push({
        path: "/editNotificationClone/" + row.id,
      });
    },
    handleRevoke(row) {
      const h = this.$createElement;
      this.$msgbox({
        message: h("div", { style: "padding:15px 0" }, [
          h(
            "p",
            {
              style:
                "color:#003448;text-align:center;font-size:20px;font-weight:bold;line-height: 1.5;"
            },
            "Are you sure to revoke this record?"
          )
        ]),
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        confirmButtonClass: "msg_button_half",
        cancelButtonClass: "msg_button_half",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        callback: (action, instance) => {
          let button = action;
          if (button == "confirm") {
            notificationRevoke(row.id).then(res => {
              if (res.data.status == "00") {
                this.$message.success("Reocrd is revoked");
                this.getList();
              } else {
                this.$message.error(res.data.msg);
              }
            });
          }
        }
      });
    },
    submitSearch() {
      this.currentPage = 1;
      this.getList();
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.regionList = [];
      this.getList();
    },
    handleTableRegion(arr) {
      let str = "";
      arr.forEach(item => {
        let name = this.$commonjs.regionMap(item);
        if (str) {
          str = str + ", " + name;
        } else {
          str = str + name;
        }
      });
      return str;
    },
    handleCommand(command) {
      this.sort = command;
      this.submitSearch();
    }
  },
  created: function() {
    this.$emit("header", true);
    this.$commonjs.initRegionsMap();
    this.getList();
    this.getFilterConfig();
    if (this.$store.state.userLevel >= 2) {
      this.formFilter.salesOffice = this.$store.state.salesOffices[0];
      this.changeSalesOffice(this.formFilter.salesOffice);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name == "editNotification") {
      from.meta.keepAlive = true;
    } else {
      from.meta.keepAlive = false;
    }
    next();
  },
  activated() {
    if (this.$route.params.update) {
      this.getList();
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.search-toggle {
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  float: right;
  margin-right: 20px;
  line-height: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #003448;
  cursor: pointer;
}
.search-toggle .el-icon-arrow-up {
  transition: transform 0.2s ease;
}
.search-toggle .el-icon-arrow-up.is-collapsed {
  transform: rotate(180deg);
}

.searchBox {
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 20px;
}

.search-grid /deep/ .el-form-item {
  margin-bottom: 16px;
}

.search-grid /deep/ .el-input__inner {
  width: 100%;
}

.search-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 12px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6ebee;
}

.search-toolbar__actions .el-button {
  min-width: 96px;
}

.search-toolbar__extra {
  display: flex;
  align-items: center;
  column-gap: 20px;
}

/* Toolbar, table & pagination merged into one panel instead of three separate cards. */
.client-panel {
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
  overflow: hidden;
}

.client-panel__toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-bottom: 1px solid #e6ebee;
}

.client-panel /deep/ .el-table {
  border: none;
  border-radius: 0;
}

.client-panel /deep/ .app-pagination {
  margin-top: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid #e6ebee;
}

.filterButton {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.sortbox {
  cursor: pointer;
}
.statusBox {
  display: flex;
  align-items: center;
}

.btnFlex {
  display: flex;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/components/notification/notification.vue